import React from 'react';
import { 
  LayoutDashboard, 
  Upload, 
  Brain, 
  MessageSquare, 
  BarChart3, 
  Settings, 
  Calendar,
  Users,
  Building2,
  LogOut,
  Shield
} from 'lucide-react';
import { User } from '../../types';

interface SidebarProps {
  user: User;
  activeView: string;
  onViewChange: (view: string) => void;
  onLogout: () => void;
}

const menuItems: Record<string, { id: string; label: string; icon: React.ComponentType<{ className?: string }> }[]> = {
  'super-admin': [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'tenant-setup', label: 'Tenant Setup', icon: Building2 },
    { id: 'tenant-dashboard', label: 'Tenant Info', icon: Building2 },
    { id: 'database-status', label: 'Database Status', icon: Settings },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'predictions', label: 'AI Predictions', icon: Brain },
    { id: 'model-testing', label: 'Model Testing', icon: Brain },
    { id: 'messaging', label: 'Messages', icon: MessageSquare },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings }
  ],
  admin: [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'predictions', label: 'AI Predictions', icon: Brain },
    { id: 'model-testing', label: 'Model Testing', icon: Brain },
    { id: 'messaging', label: 'Messages', icon: MessageSquare },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'settings', label: 'Settings', icon: Settings }
  ],
  'data-manager': [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'upload', label: 'Data Upload', icon: Upload },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'model-testing', label: 'Model Testing', icon: Brain },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ],
  'integration-user': [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'integrations', label: 'Integrations', icon: Settings },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ]
};

export const Sidebar: React.FC<SidebarProps> = ({ user, activeView, onViewChange, onLogout }) => {
  const role = typeof user.role === 'string' ? user.role : 'admin';
  const items = menuItems[role] ?? menuItems.admin;
  const isSuperAdmin = role === 'super-admin';

  return (
    <div className="bg-slate-900 text-white w-64 min-h-screen flex flex-col">
      <div className="p-6 border-b border-slate-700">
        <h1 className="text-xl font-bold text-blue-400">AttendAI</h1>
        <p className="text-sm text-slate-400 mt-1">Healthcare Analytics</p>
      </div>
      
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSuperAdmin ? 'bg-amber-600' : 'bg-blue-600'}`}>
            {isSuperAdmin ? <Shield className="w-5 h-5" /> : <Users className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <p className="font-medium truncate">{user.name}</p>
            <p className={`text-xs capitalize ${isSuperAdmin ? 'text-amber-400' : 'text-slate-400'}`}>
              {role.replace(/-/g, ' ')}
            </p>
            {user.tenantName && (
              <p className="text-xs text-blue-400 truncate mt-0.5">{user.tenantName}</p>
            )}
          </div>
        </div>
      </div>

      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => onViewChange(item.id)}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                  activeView === item.id 
                    ? 'bg-blue-600 text-white' 
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="p-4 border-t border-slate-700">
        <button
          onClick={onLogout}
          className="w-full flex items-center space-x-3 p-3 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};
